import { useMemo } from 'react';
import type { BriefContextSectionDto } from '@inference.engine/api-client';
import type { ArtifactKind } from '@inference.engine/widgets';
import { useQuery } from '@tanstack/react-query';
import type { WorkspaceArtifactRow } from '@/entities/artifact';
import { briefsQueryOptions } from '@/entities/brief';
import { projectArtifactsQuery } from '@/entities/project-artifact';

/**
 * One round-trip page is plenty for the picker: the merged list is filtered +
 * searched client-side, and the picker has no "Load more" affordance in the
 * project flow. Matches the briefs list-view page size.
 */
const PAGE_SIZE = 100;

/**
 * A picker row. Brief rows additionally carry their TipTap `sections` so the
 * composer can forward each brief's content inline on the wire (the
 * inference-api must not call workspace-api to resolve briefs). Artifact rows
 * omit `sections` — they ride the wire as ids only.
 */
type ProjectContextRow = WorkspaceArtifactRow & {
  sections?: BriefContextSectionDto[];
};

interface UseProjectContextRowsResult {
  rows: ProjectContextRow[];
  isLoading: boolean;
}

/**
 * Merges a project's Briefs (`GET /projects/:id/briefs`) and Library Artifacts
 * (`GET /projects/:id/artifacts`) into a single `WorkspaceArtifactRow[]` the
 * shared `PickerTable` renders.
 *
 * Dedup rule (parent contract): real Briefs come from the briefs endpoint; the
 * artifacts endpoint ALSO returns `kind=brief` rows for the same briefs, so we
 * EXCLUDE `kind=brief` artifacts to avoid doubling.
 *
 * Default ordering: newest-first (`createdAt` DESC); ties broken by lowest id
 * (matches `ExistingArtifactsPicker`'s server-side default).
 *
 * The hook is gated on `projectId`: with no project selected both queries stay
 * dormant and `rows` is empty, so the host renders its "Choose a project" empty
 * state.
 */
export function useProjectContextRows(projectId: string | undefined): UseProjectContextRowsResult {
  const briefsQuery = useQuery(
    briefsQueryOptions({
      projectId: projectId ?? '',
      page: 1,
      pageSize: PAGE_SIZE,
    }),
  );

  const artifactsQuery = useQuery(projectArtifactsQuery(projectId, { take: PAGE_SIZE }));

  const rows = useMemo<ProjectContextRow[]>(() => {
    if (!projectId) return [];

    const briefRows: ProjectContextRow[] = (briefsQuery.data?.items ?? []).map((brief) => ({
      id: brief.id,
      key: brief.id,
      contextId: brief.projectId,
      name: brief.title,
      kind: 'brief',
      version: null,
      thumbnailUrl: null,
      createdAt: brief.createdAt,
      contextName: null,
      creatorType: null,
      // Retain each section's heading + TipTap JSON so the composer can forward
      // the brief content inline on the wire. The BE persists `null` content for
      // AI-skipped sections; default to `{}` to satisfy the required wire field.
      sections: brief.sections.map((section) => ({
        name: section.name,
        content: section.content ?? {},
      })),
    }));

    const artifactRows: ProjectContextRow[] = (artifactsQuery.data?.items ?? [])
      // Dedup: the artifacts endpoint surfaces briefs as `kind=brief`; the
      // briefs endpoint is the source of truth for those, so drop them here.
      .filter((artifact) => artifact.artifactKind !== 'brief')
      .map((artifact) => ({
        id: artifact.artifactId ?? artifact.id,
        key: artifact.artifactKey ?? artifact.id,
        contextId: artifact.originatingContextId ?? artifact.projectId,
        name: artifact.artifactName,
        kind: artifact.artifactKind as ArtifactKind,
        version: artifact.artifactVersion,
        thumbnailUrl: artifact.thumbnailUrl,
        createdAt: artifact.attachedAt,
        contextName: artifact.originatingContextName ?? null,
        creatorType: artifact.creatorType ?? null,
      }));

    // Ordering is owned by the picker's `sortRows` (its default already matches
    // newest-first + id tiebreak), so just concat here and let the picker own
    // the single sort — avoids re-sorting the full list on every query change.
    return [...briefRows, ...artifactRows];
  }, [projectId, briefsQuery.data, artifactsQuery.data]);

  return {
    rows,
    isLoading: Boolean(projectId) && (briefsQuery.isLoading || artifactsQuery.isLoading),
  };
}
